import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const LocationPOSInterface = ({
  locationId,
  locationName,
  products,
  categories,
  cart,
  onAddToCart,
  onUpdateQuantity,
  onRemoveFromCart,
  onClearCart,
  onCheckout
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [processing, setProcessing] = useState(false);
  const [creditCardLast4, setCreditCardLast4] = useState('');

  const filteredProducts = products?.filter(product => {
    const matchesSearch = product?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         product?.barcode?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product?.category_id === selectedCategory;
    const inStock = product?.stock_quantity > 0;
    return matchesSearch && matchesCategory && inStock;
  });

  const subtotal = cart?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) || 0;
  const tax = subtotal * 0.08; // 8% tax rate
  const total = subtotal + tax;

  const handleCheckoutClick = async () => {
    if (cart?.length === 0) return;

    setProcessing(true);
    const paymentData = {
      payment_method: paymentMethod,
      subtotal,
      tax,
      total,
      credit_card_last4: paymentMethod === 'credit_card' ? creditCardLast4 : null
    };

    const result = await onCheckout(paymentData);
    setProcessing(false);

    if (result?.success) {
      setCreditCardLast4('');
      setPaymentMethod('cash');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Product Catalog */}
      <div className="lg:col-span-2 space-y-4">
        <div className="card">
          <h2 className="text-xl font-semibold text-foreground mb-4">Product Catalog - {locationName}</h2>
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search products or scan barcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                icon="Search"
              />
            </div>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="w-full md:w-48"
            >
              <option value="all">All Categories</option>
              {categories?.map(cat => (
                <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredProducts?.map(product => (
            <div key={product?.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  {product?.image_url ? (
                    <img src={product?.image_url} alt={product?.name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Icon name="Package" size={32} color="var(--color-muted-foreground)" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground mb-1">{product?.name}</h3>
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{product?.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary">${product?.price?.toFixed(2)}</span>
                    <Button
                      size="sm"
                      onClick={() => onAddToCart(product)}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Stock: {product?.stock_quantity}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts?.length === 0 && (
          <div className="card text-center py-12">
            <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">No products available</p>
          </div>
        )}
      </div>
      {/* Cart & Checkout */}
      <div className="lg:col-span-1">
        <div className="card sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Cart</h2>
            {cart?.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={onClearCart}
                iconName="X"
              >
                Clear
              </Button>
            )}
          </div>

          {cart?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="ShoppingCart" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-muted-foreground">Cart is empty</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {cart?.map(item => (
                  <div key={item?.id} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{item?.name}</p>
                        <p className="text-xs text-muted-foreground">${item?.price?.toFixed(2)} each</p>
                      </div>
                      <button
                        onClick={() => onRemoveFromCart(item?.id)}
                        className="text-error hover:text-error/80"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUpdateQuantity(item?.id, item?.quantity - 1)}
                        iconName="Minus"
                      />
                      <span className="text-sm font-medium text-foreground w-12 text-center">{item?.quantity}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUpdateQuantity(item?.id, item?.quantity + 1)}
                        iconName="Plus"
                      />
                      <span className="ml-auto text-sm font-semibold text-foreground">
                        ${(item?.price * item?.quantity)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4 space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground font-medium">${subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax (8%)</span>
                  <span className="text-foreground font-medium">${tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                  <span className="text-foreground">Total</span>
                  <span className="text-primary">${total?.toFixed(2)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e?.target?.value)}
                  className="w-full"
                >
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="member_account">Member Account</option>
                </Select>

                {paymentMethod === 'credit_card' && (
                  <Input
                    type="text"
                    placeholder="Last 4 digits"
                    value={creditCardLast4}
                    onChange={(e) => setCreditCardLast4(e?.target?.value)}
                    maxLength={4}
                  />
                )}

                <Button
                  onClick={handleCheckoutClick}
                  disabled={processing || cart?.length === 0}
                  className="w-full"
                  iconName="CreditCard"
                  iconPosition="left"
                >
                  {processing ? 'Processing...' : 'Complete Sale'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationPOSInterface;